import { Component, inject } from '@angular/core';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import {
    CreateCardOption,
    type ProductButtonFormType,
    type ProductCardDataFormType,
    type ProductCardWidgetData,
    type VariantFormGroup,
} from '@web/widgets/product-card/type';
import {
    cardSettingsFormGroup,
    imageFormControl,
    modalSettingsFormGroup,
    parameterFormGroup,
    previewModalSettingsFormGroup,
    productButtonFormGroup,
    productCardFormGroup,
    propertyFormGroup,
    sectionFormGroup,
    variantFormGroup,
} from '@web/widgets/product-card/form/product-card-widget.form';
import { cardTypeOptions } from '@web/widgets/product-card/constants/defaults-product-card.conts';

import { BsModalService } from 'ngx-bootstrap/modal';
import { CreateProductModalComponent } from '@web/widgets/product-card/components/product-card-control/create-product-modal/create-product-modal.component';
import { CatalogModalComponent } from '@web/widgets/product-card/components/product-card-control/catalog-modal/catalog-modal.component';
import { type ProductCard } from '@common/types/product-card.type';
import type { WidgetErrors } from '@common/types/widget-errors.type';
import type { WebLegacyWidgetEntity } from '@web/types';
import { TranslateModule } from '@ngx-translate/core';
import { ModalSettingsComponent } from './modal-settings/modal-settings.component';
import { PreviewModalSettingsComponent } from './preview-modal-settings/preview-modal-settings.component';
import { ProductSettingsComponent } from './product-settings/product-settings.component';
import { ButtonSettingsComponent } from './button-settings/button-settings.component';
import { FormsModule } from '@angular/forms';
import { CardSettingsComponent } from './card-settings/card-settings.component';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

import { Observable } from 'rxjs';

export enum CurrentSettings {
    MAIN = 'main',
    BUTTON = 'button',
    CARD = 'card',
    MODAL = 'modal',
    PREVIEW = 'preview',
    SHOW_MORE_BUTTON = 'show_more_button',
}

@Component({
    selector: 'web-product-card-control',
    templateUrl: './product-card-control.component.html',
    styleUrls: ['./product-card-control.component.less'],
    imports: [
        WebSettingsModule,
        CardSettingsComponent,
        FormsModule,
        ButtonSettingsComponent,
        ProductSettingsComponent,
        PreviewModalSettingsComponent,
        ModalSettingsComponent,
        TranslateModule,
    ],
})
export class ProductCardControlComponent extends WebBaseControlComponent<ProductCardWidgetData> {
    protected readonly modalService: BsModalService = inject(BsModalService);
    public currentSettings: CurrentSettings = CurrentSettings.MAIN;
    public settingEnum = CurrentSettings;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group({
                element: productCardFormGroup(),
                cardViewType: this.fb.control(null),
                isShowCardSettings: this.fb.control(false),
                cardKindViewType: this.fb.control(null),
                cardModalViewType: this.fb.control(null),
                basketButton: productButtonFormGroup(),
                showMoreButton: productButtonFormGroup(),
                cardSettings: cardSettingsFormGroup(),
                previewModalSettings: previewModalSettingsFormGroup(),
                modalSettings: modalSettingsFormGroup(),
                previewHorizontalPosition: this.fb.control(null),
                createCardOption: this.fb.control(CreateCardOption.create),
                selectedFromCatalog: this.fb.control(false),
            }),
        });
    }

    protected setFormData(): void {
        this.setFormArrays(this.widgetData?.element);

        this.controlForm.patchValue({ data: this.widgetData }, { emitEvent: false });
        if (!this.widget?.displayId) {
            this.updateWidgetDisplayId(this.displayId);
        }
    }

    public openCatalogModal() {
        const modalRef = this.modalService.show(CatalogModalComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
        });

        modalRef.content.onClose.subscribe((element) => {
            if (!element) return;

            this.setFormArrays(element, true);
            this.data.patchValue({ element: element, isShowCardSettings: true, selectedFromCatalog: true });
            this.changeDetectorRef.detectChanges();
        });
    }

    public openProductModal(isEditCard: boolean = false) {
        const modalRef = this.modalService.show(CreateProductModalComponent, {
            ignoreBackdropClick: true,
            class: 'modal-lg',
            initialState: {
                widgetData: this.widgetData,
                isEditCard,
                widgetId: this.widget.id,
            },
        });

        modalRef.content.onClose.subscribe((result) => {
            if (!result) return;

            this.setFormArrays(result, true);
            this.data.patchValue({ element: result, isShowCardSettings: true, selectedFromCatalog: false });
            this.changeDetectorRef.detectChanges();
        });
    }

    /** The CRM no longer has the bound product, so the panel offers to pick a replacement. */
    public get isRemovedFromCatalog(): boolean {
        return !!this.widgetData?.element?.isRemovedFromCatalog;
    }

    /**
     * Called by `WidgetsErrorsEffects` on a bare component instance (no inputs, no form) —
     * must stay derivable from the widget value alone.
     *
     * A product deleted in the CRM is reported as a widget error so it gets the warning badge on the
     * canvas and shows up in the confirmation before saving or publishing. It does not block either:
     * the page is still publishable, the widget simply will not be rendered on the site.
     */
    public validate<D extends WebLegacyWidgetEntity>(widget: D): WidgetErrors {
        const element = (widget.data as ProductCardWidgetData)?.element;

        if (!element?.isRemovedFromCatalog) {
            return null;
        }

        return [{ key: this.translateService.instant('widget_product_removed_from_catalog') }];
    }

    public setCurrentSettingsType(val: CurrentSettings): void {
        this.currentSettings = val;
    }

    public setCreateCardOption(value: CreateCardOption): void {
        this.createCardOption.patchValue(+value);
    }

    private setImages(element: ProductCard) {
        if (element?.images.length !== this.element.controls.images.length) {
            this.element.controls.images.clear({ emitEvent: false });
            element?.images.forEach(() => {
                this.element.controls.images.push(imageFormControl(null), { emitEvent: false });
            });
        }
    }

    private setSections(element: ProductCard, force = false) {
        if (!element?.sections.length) {
            this.element.controls.sections.clear({ emitEvent: false });
            this.element.controls.sections.push(sectionFormGroup(), { emitEvent: false });
            return;
        }

        if (element?.sections.length !== this.element.controls.sections.length || force) {
            this.element.controls.sections.clear({ emitEvent: false });

            element?.sections.forEach((section) => {
                const formGroup = sectionFormGroup();

                if (section.parameters.length !== formGroup.controls.parameters.length) {
                    formGroup.controls.parameters.clear({ emitEvent: false });
                    section.parameters.forEach(() => {
                        formGroup.controls.parameters.push(parameterFormGroup(), { emitEvent: false });
                    });
                }

                this.element.controls.sections.push(formGroup, { emitEvent: false });
            });
        }
    }

    private setVariants(element: ProductCard, force = false) {
        if (element?.variants?.length !== this.element.controls?.variants?.length || force) {
            this.element.controls.variants.clear({ emitEvent: false });

            element?.variants?.forEach((variant) => {
                const formGroup: VariantFormGroup = variantFormGroup();

                variant?.images.forEach(() => {
                    formGroup.controls.images.push(imageFormControl(null), { emitEvent: false });
                });

                this.element.controls.variants.push(formGroup);
            });
        }
    }

    private setProperties(element: ProductCard) {
        if (element?.properties?.length !== this.element.controls?.properties?.length) {
            this.element.controls.properties.clear({ emitEvent: false });

            element?.properties?.forEach(() => {
                const formGroup = propertyFormGroup();

                this.element.controls.properties.push(formGroup);
            });
        }
    }

    private setFormArrays(element: ProductCard, force = false) {
        this.setImages(element);
        this.setSections(element, force);
        this.setVariants(element, force);
        this.setProperties(element);
    }

    public get element(): ProductCardDataFormType['element'] {
        return this.data.get('element') as ProductCardDataFormType['element'];
    }

    public get basketButton(): ProductCardDataFormType['basketButton'] {
        return this.data.get('basketButton') as ProductCardDataFormType['basketButton'];
    }

    public get showMoreButton(): ProductCardDataFormType['showMoreButton'] {
        return this.data.get('showMoreButton') as ProductCardDataFormType['showMoreButton'];
    }

    public get buttonLabel(): ProductButtonFormType['label'] {
        return this.basketButton.get('label') as ProductButtonFormType['label'];
    }

    public get cardSettings(): ProductCardDataFormType['cardSettings'] {
        return this.data.get('cardSettings') as ProductCardDataFormType['cardSettings'];
    }

    public get previewModalSettings(): ProductCardDataFormType['previewModalSettings'] {
        return this.data.get('previewModalSettings') as ProductCardDataFormType['previewModalSettings'];
    }

    public get modalSettings(): ProductCardDataFormType['modalSettings'] {
        return this.data.get('modalSettings') as ProductCardDataFormType['modalSettings'];
    }

    public get isShowCardSettings(): boolean {
        return this.data.get('isShowCardSettings').value;
    }

    public get createCardOption(): ProductCardDataFormType['createCardOption'] {
        return this.data.get('createCardOption') as ProductCardDataFormType['createCardOption'];
    }

    public get isNewCardOption(): boolean {
        return (this.createCardOption.value as CreateCardOption) === CreateCardOption.create;
    }

    public get isCatalogOption(): boolean {
        return (this.createCardOption.value as CreateCardOption) === CreateCardOption.catalog;
    }

    public get cardKindViewType(): ProductCardDataFormType['cardKindViewType'] {
        return this.data.get('cardKindViewType') as ProductCardDataFormType['cardKindViewType'];
    }

    public getWidgetId(widget) {
        return widget.displayId ? widget.displayId : `widget-${widget.id}`;
    }

    protected readonly cardTypeOptions = cardTypeOptions;
    protected readonly CreateCardOption = CreateCardOption;
}
